/**
 * Provider List Component
 * 
 * Displays a list of providers with search, filtering, and management capabilities.
 * Uses reusable components for toolbar, status toggle, and data display.
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useSmartOSearch } from 'oreacto';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import StickyFlexTable from '../common/StickyFlexTable';
import ThemedTooltip from '../common/ThemedTooltip';
import ThemedChip from '../common/ThemedChip';
import ManagementToolbar from '../common/ManagementToolbar';
import StatusToggleButton from '../common/StatusToggleButton';
import StatusToggleDialog from '../common/StatusToggleDialog';
import ListModal from '../common/ListModal';
import { useTheme } from '../../ThemeContext';
import IconButton from '@mui/material/IconButton';
import { useDetectMyRole, useStatusToggle } from '../../custom-hooks';
import ProviderService from '../../services/provider-service';
import { 
  PROVIDER_FILTER_GROUPS, 
  SEARCH_PLACEHOLDERS, 
  MODULE_PERMISSIONS 
} from '../../constants/filter-constants';

/**
 * ProviderList Component
 * 
 * @param {Object} props - Component props
 * @param {Function} props.setRenderType - Function to change render type
 * @param {string} props.renderType - Current render type
 * @param {Array} props.providers - Array of provider objects
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onRefresh - Refresh handler
 * @param {Object} props.facilityMap - Map of facility IDs to names
 * @param {Object} props.selectedFilters - Current filter state
 * @param {Function} props.onFiltersChange - Filter change handler
 * @returns {JSX.Element} ProviderList component
 */
const ProviderList = ({ 
  setRenderType, 
  renderType, 
  providers = [], 
  loading, 
  onEdit,
  onAdd, // Add the proper onAdd prop
  onRefresh, 
  facilityMap = {},
  selectedFilters,
  onFiltersChange,
  // Legacy props (kept for compatibility)
  setInitialValues,
  onDelete
}) => {
  const { currentTheme } = useTheme();
  const { role } = useDetectMyRole();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states for Specialties
  const [specialtiesModalOpen, setSpecialtiesModalOpen] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [selectedProviderName, setSelectedProviderName] = useState('');

  // Modal states for Affiliations
  const [affiliationsModalOpen, setAffiliationsModalOpen] = useState(false);
  const [selectedAffiliations, setSelectedAffiliations] = useState([]);

  // Use status toggle hook
  const {
    isDialogOpen: statusDialogOpen,
    isLoading: statusLoading,
    entityToToggle: providerToToggle,
    toggleAction,
    handleToggleClick,
    handleConfirm: handleStatusToggleConfirm,
    handleCancel: handleStatusToggleCancel
  } = useStatusToggle(ProviderService.toggleStatus, onRefresh, 'provider');

  // Check permissions
  const canEditProvider = MODULE_PERMISSIONS.provider.edit.includes(role);

  /**
   * Apply filters to providers based on selected filters
   * 
   * @param {Array} providersData - Raw providers data
   * @returns {Array} Filtered providers
   */
  const applyFilters = (providersData) => {
    return providersData.filter((provider) => {
      const statusFilter = selectedFilters?.status;
      if (statusFilter === 'active' && provider.isActive !== true) return false;
      if (statusFilter === 'inactive' && provider.isActive !== false) return false;
      return true;
    });
  };

  // Apply filters to providers
  const filteredProvidersData = applyFilters(providers);

  // Use smart search hook
  const { filteredItems, query, setQuery } = useSmartOSearch({
    items: filteredProvidersData,
    filterKeys: [
      'fullName',
      'npi',
      'taxId',
      'specialties'
    ],
    searchQuery: '',
    sortKey: 'fullName',
    sortOrder: 'asc'
  });

  /**
   * Reset pagination to first page when filters or search query change
   * This ensures user doesn't stay on a page that no longer exists
   */
  useEffect(() => {
    setPage(0);
  }, [selectedFilters, query]);

  const filteredProviders = query ? filteredItems : filteredProvidersData;

  /**
   * Handles showing all specialties in modal
   * 
   * @param {Array} specialties - Array of specialties
   * @param {string} providerName - Provider name for modal subtitle
   */
  const handleShowAllSpecialties = (specialties, providerName) => {
    setSelectedSpecialties(specialties);
    setSelectedProviderName(providerName);
    setSpecialtiesModalOpen(true);
  };

  /**
   * Handles showing all affiliations in modal
   * 
   * @param {Array} affiliations - Array of facility names
   * @param {string} providerName - Provider name for modal subtitle
   */
  const handleShowAllAffiliations = (affiliations, providerName) => {
    setSelectedAffiliations(affiliations);
    setSelectedProviderName(providerName);
    setAffiliationsModalOpen(true);
  };

  /**
   * Renders specialties as chips with smart truncation (max 3 chips)
   * 
   * @param {Array} specialties - Array of specialties
   * @param {string} providerName - Provider name for modal
   * @returns {JSX.Element} Rendered specialties
   */
  const renderSpecialties = (specialties, providerName) => {
    if (!Array.isArray(specialties) || specialties.length === 0) {
      return <span style={{ fontSize: 13, opacity: 0.7 }}>No specialties listed</span>;
    }

    // Show all if 3 or fewer items
    if (specialties.length <= 3) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {specialties.map((specialty, idx) => (
            <ThemedChip
              key={idx}
              variant="default"
              size="small"
            >
              {specialty}
            </ThemedChip>
          ))}
        </Box>
      );
    }

    // Show 2 items + "+X more" if more than 3 items (max 3 chips)
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {specialties.slice(0, 2).map((specialty, idx) => (
          <ThemedChip
            key={idx}
            variant="default"
            size="small"
          >
            {specialty}
          </ThemedChip>
        ))}
        <ThemedTooltip 
          title="Click to see all specialties" 
          placement="top"
        >
          <ThemedChip
            variant="info"
            size="small"
            onClick={() => handleShowAllSpecialties(specialties, providerName)}
            sx={{ cursor: 'pointer' }}
          >
            +{specialties.length - 2} more
          </ThemedChip>
        </ThemedTooltip>
      </Box>
    );
  };

  /**
   * Renders affiliations with facility names (max 3 chips)
   * 
   * @param {Array} affiliations - Array of affiliations
   * @param {string} providerName - Provider name for modal
   * @returns {JSX.Element} Rendered affiliations
   */
  const renderAffiliations = (affiliations, providerName) => {
    if (!Array.isArray(affiliations) || affiliations.length === 0) {
      return <span style={{ fontSize: 13, opacity: 0.7 }}>No affiliations listed</span>;
    }

    // Get unique facility names, filtering out inactive facilities
    const uniqueFacilities = new Set(affiliations.map(affiliation => affiliation.facilityId));
    const uniqueFacilitiesArray = Array.from(uniqueFacilities);
    const facilityNames = uniqueFacilitiesArray
      .map(facilityId => facilityMap[facilityId])
      .filter(name => name !== undefined);

    // If no active facilities found, show appropriate message
    if (facilityNames.length === 0) {
      return <span style={{ fontSize: 13, opacity: 0.7 }}>Not affiliated with any active facility</span>;
    }

    // Show all if 3 or fewer items
    if (facilityNames.length <= 3) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {facilityNames.map((name, idx) => (
            <ThemedChip
              key={idx}
              variant="default"
              size="small"
            >
              {name}
            </ThemedChip>
          ))}
        </Box>
      );
    }

    // Show 2 items + "+X more" if more than 3 items (max 3 chips)
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {facilityNames.slice(0, 2).map((name, idx) => (
          <ThemedChip
            key={idx}
            variant="default"
            size="small"
          >
            {name}
          </ThemedChip>
        ))}
        <ThemedTooltip 
          title="Click to see all affiliations" 
          placement="top"
        >
          <ThemedChip
            variant="info"
            size="small"
            onClick={() => handleShowAllAffiliations(facilityNames, providerName)}
            sx={{ cursor: 'pointer' }}
          >
            +{facilityNames.length - 2} more
          </ThemedChip>
        </ThemedTooltip>
      </Box>
    );
  };

  /**
   * Handles adding a new provider
   */
  const handleAddProvider = () => {
    // Use the onAdd prop if provided (recommended), otherwise fallback to legacy behavior
    if (onAdd) {
      onAdd(); // This will call the CRUD hook's handleAdd which properly clears form state
    } else {
      // Legacy fallback
      setRenderType('form');
      if (setInitialValues) setInitialValues(null);
    }
  };

  // Table columns configuration
  const columns = [
    {
      id: 'fullName',
      label: 'Name',
      minWidth: 180,
      fixed: true,
      renderCell: (row) => row.fullName,
    },
    {
      id: 'npi',
      label: 'NPI',
      minWidth: 140,
      renderCell: (row) => row.npi,
    },
    {
      id: 'taxId',
      label: 'Tax ID',
      minWidth: 140,
      renderCell: (row) => row.taxId,
    },
    {
      id: 'specialties',
      label: 'Specialties',
      minWidth: 200,
      renderCell: (row) => renderSpecialties(row.specialties, row.fullName),
    },
    {
      id: 'affiliations',
      label: 'Affiliations',
      minWidth: 200,
      renderCell: (row) => renderAffiliations(row.affiliations, row.fullName),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      renderCell: (row) => (
        <ThemedChip
          variant={row.isActive ? "success" : "warning"}
          size="small"
        >
          {row.isActive ? "Active" : "Inactive"}
        </ThemedChip>
      ),
    },
    {
      id: "action",
      label: "Action",
      minWidth: 120,
      fixed: true,
      align: "right",
      renderCell: (row) => (
        <>
          <ThemedTooltip 
            title={
              canEditProvider 
                ? "Edit provider information" 
                : "Administrator privileges required to modify provider records"
            }
            placement="top"
          >
            <span>
              <IconButton
                aria-label="edit"
                onClick={() => canEditProvider && onEdit && onEdit(row)}
                size="small"
                disabled={!canEditProvider}
                sx={{ 
                  color: canEditProvider 
                    ? currentTheme.text.primary 
                    : currentTheme.text.disabled || '#999',
                  cursor: canEditProvider ? 'pointer' : 'not-allowed'
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </ThemedTooltip>
          <StatusToggleButton
            entity={row}
            module="provider"
            onToggle={handleToggleClick}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <StickyFlexTable
        columns={columns}
        rows={filteredProviders}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        toolbar={
          <ManagementToolbar
            module="provider"
            searchQuery={query}
            onSearchChange={setQuery}
            searchPlaceholder={SEARCH_PLACEHOLDERS.provider}
            filterGroups={PROVIDER_FILTER_GROUPS}
            selectedFilters={selectedFilters}
            onFiltersChange={onFiltersChange}
            onAdd={handleAddProvider}
            addButtonLabel="Add Provider"
          />
        }
      />

      {/* Status Toggle Dialog */}
      <StatusToggleDialog
        open={statusDialogOpen}
        entity={providerToToggle}
        action={toggleAction}
        module="provider"
        onConfirm={handleStatusToggleConfirm}
        onCancel={handleStatusToggleCancel}
        loading={statusLoading}
      />

      {/* Specialties Modal */}
      <ListModal
        open={specialtiesModalOpen}
        title="Specialties"
        subtitle={`Provider: ${selectedProviderName}`}
        data={selectedSpecialties}
        onClose={() => setSpecialtiesModalOpen(false)}
        maxWidth={500}
        showIndex={true}
      />

      {/* Affiliations Modal */}
      <ListModal
        open={affiliationsModalOpen}
        title="Affiliations"
        subtitle={`Provider: ${selectedProviderName}`}
        data={selectedAffiliations}
        onClose={() => setAffiliationsModalOpen(false)}
        maxWidth={500}
        showIndex={true}
      />
    </>
  );
};

export default ProviderList;